import { CSSProperties, useEffect, useState } from 'react';
import { differenceInSeconds } from 'date-fns';
import { AdviceStatuses, AmlStatuses, ClientStatuses, ConfirmationType, SearchClientDto } from '../../api';
import { applicationNeedsConfirmation } from './Helpers';
import { CompletedChip, FailedChip, InProgressChip, NotStartedChip } from './IconsAndChips';

export const SatStatus = ({ adviceStatus, title, createdDate, adviceInProgressDate, adviceCompletedDate }: {
    adviceStatus: AdviceStatuses;
    title: string;
    createdDate?: Date;
    adviceInProgressDate?: Date;
    adviceCompletedDate?: Date;
}) => {
    switch (adviceStatus) {
        case AdviceStatuses.None:
            return <NotStartedChip title={title} toolTipText={`Not started ${getElapsedTime(createdDate ? differenceInSeconds(new Date(), new Date(createdDate)) : 0)}`} />
        case AdviceStatuses.InProgress:
            return <InProgressChip title={title} toolTipText={`In progress ${getElapsedTime(adviceInProgressDate ? differenceInSeconds(new Date(), new Date(adviceInProgressDate)) : 0)}`} />
        case AdviceStatuses.Completed:
            return <CompletedChip title={title} toolTipText={`Completed ${getElapsedTime(adviceCompletedDate ? differenceInSeconds(new Date(), new Date(adviceCompletedDate)) : 0)}`} />
        default: // don't display if skipped or direct
            return <></>
    }
}

export const ApplicationAmlStatus = ({ title, adviceStatus, amlStatus, clientStatus, style, amlFailedDate, applicationCompletedDate, clientInProgressDate, adviceCompletedDate, isRetention }: {
    title: string;
    adviceStatus: AdviceStatuses;
    amlStatus: AmlStatuses;
    clientStatus?: ClientStatuses;
    style?: CSSProperties;
    amlFailedDate?: Date;
    applicationCompletedDate?: Date;
    clientInProgressDate?: Date;
    adviceCompletedDate?: Date;
    isRetention?: boolean;
}) => {
    const [clientAmlState, setClientAmlState] = useState<AmlStatuses>(AmlStatuses.None);
    const chipLabel = isRetention ? 'Retained' : title;

    useEffect(() => {
        const amlStatuses = amlStatus.split(', ');
        if (amlStatuses.includes(AmlStatuses.Successful)) setClientAmlState(AmlStatuses.Successful);
        else if (amlStatuses.includes(AmlStatuses.Failed)) setClientAmlState(AmlStatuses.Failed);
        else setClientAmlState(AmlStatuses.None);
    }, [amlStatus]);
    
    if (adviceStatus !== AdviceStatuses.Skipped && adviceStatus !== AdviceStatuses.Completed && adviceStatus !== AdviceStatuses.Direct) return null;
 
    if (clientStatus === ClientStatuses.InProgress || (clientStatus === ClientStatuses.Completed && clientAmlState === AmlStatuses.None) && !isRetention)
        return <InProgressChip title={title} customStyle={style} toolTipText={`In progress ${getElapsedTime(clientInProgressDate ? differenceInSeconds(new Date(), new Date(clientInProgressDate)) : 0)}`} />
     
    if (clientStatus === ClientStatuses.Completed && clientAmlState === AmlStatuses.None && !isRetention)
        return <InProgressChip title={title} customStyle={style} toolTipText={`Processing ${getElapsedTime(applicationCompletedDate ? differenceInSeconds(new Date(), new Date(applicationCompletedDate)) : 0)}`} />
     
    if (clientAmlState === AmlStatuses.Failed && !isRetention)
        return <FailedChip title={title} customStyle={style} toolTipText={`Failed ${getElapsedTime(amlFailedDate ? differenceInSeconds(new Date(), new Date(amlFailedDate)) : 0)}`} />
 
    if (clientAmlState === AmlStatuses.Successful || (clientStatus === ClientStatuses.Completed && isRetention))
        return <CompletedChip title={chipLabel} customStyle={style} toolTipText={`Completed ${getElapsedTime(applicationCompletedDate ? differenceInSeconds(new Date(), new Date(applicationCompletedDate)) : 0)}`} />
     
    return <NotStartedChip title={chipLabel} customStyle={style} toolTipText={`Not started ${getElapsedTime(adviceCompletedDate ? differenceInSeconds(new Date(), new Date(adviceCompletedDate)) : 0)}`} />
}

export const JointStatus = ({ jointStatusSummary, clientStatus, title, jointInitialEmailDate, tooltipSecondaryText }: {
    jointStatusSummary?: AmlStatuses;
    clientStatus: ClientStatuses;
    clientAmlStatus: AmlStatuses;
    title: string;
    tooltipSecondaryText: string;
    jointInitialEmailDate?: Date;
}) => {
    if (clientStatus === ClientStatuses.InProgress)
        return <InProgressChip title={title} toolTipText={`In progress ${getElapsedTime(jointInitialEmailDate ? differenceInSeconds(new Date(), new Date(jointInitialEmailDate)) : 0)} ${tooltipSecondaryText}`} customStyle={{ marginRight: 0 }} />

    switch (jointStatusSummary) {
        case AmlStatuses.None: 
            return <NotStartedChip title={title} toolTipText={`Not started ${getElapsedTime(jointInitialEmailDate ? differenceInSeconds(new Date(), new Date(jointInitialEmailDate)) : 0)} ${tooltipSecondaryText}`} customStyle={{ marginRight: 0 }} />
        case AmlStatuses.Successful:
            return <CompletedChip title={title} toolTipText={`Completed ${getElapsedTime(jointInitialEmailDate ? differenceInSeconds(new Date(), new Date(jointInitialEmailDate)) : 0)} ${tooltipSecondaryText}`} customStyle={{ marginRight: 0 }} />
        case AmlStatuses.Failed:
            return <FailedChip title={title} toolTipText={`Failed ${getElapsedTime(jointInitialEmailDate ? differenceInSeconds(new Date(), new Date(jointInitialEmailDate)) : 0)} ${tooltipSecondaryText}`} customStyle={{ marginRight: 0 }} />
        default:
            return <></>
    }
}

export const ConfirmationStatus = ({ client, title, customStyle }: { client: SearchClientDto; title: string; customStyle?: CSSProperties }) => {
    const needsConfirmation = applicationNeedsConfirmation(client); 
    let diffInSecs = 0;

    if (needsConfirmation) diffInSecs = client.applicationCompletedDate ? differenceInSeconds(new Date(), new Date(client.applicationCompletedDate)) : 0;
    else if (!needsConfirmation) diffInSecs = client.confirmDate ? differenceInSeconds(new Date(), new Date(client?.confirmDate)) : 0;

    if (needsConfirmation) return <FailedChip title={title} customStyle={customStyle} toolTipText={`Action required ${getElapsedTime(diffInSecs)}`} />
    else if (!needsConfirmation && client.confirmationType?.split(', ').includes(ConfirmationType.Confirmed)) return <CompletedChip title={title} customStyle={customStyle} toolTipText={`Confirm ${getElapsedTime(diffInSecs)}`} />
    else return <></>
}

const getElapsedTime = (diffInSeconds: number) => {
    const hours = diffInSeconds / 60 / 60;
    let suffix = '';
    let elapsed = 0;

    if (diffInSeconds > 0) { 
        if (hours < 2) { // up to 120 minutes
            elapsed = diffInSeconds / 60;
            suffix = `minute${elapsed < 2 ? '' : 's'}`;
        } else if (hours >= 2 && hours < 48) { // between 2-48 hours
            elapsed = hours;
            suffix = 'hours';
        } else if (hours >= 48 && hours < 360) { // between 2-14 days
            elapsed = hours / 24;
            suffix = 'days';
        } else if (hours >= 360) { // at least two weeks
            elapsed = hours / 168;
            suffix = 'weeks';
        }
    }
     
    return `${elapsed > 0 ? ' - ' + Math.floor(elapsed) + ' ' + suffix : ''}`;
}
